import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Divider,
  Empty,
  Form,
  Popconfirm,
  Radio,
  Select,
  Space,
  Table,
  Typography,
  Upload,
  message,
} from 'antd';
import { DeleteOutlined, EyeOutlined, FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { DermatoForm, LesionFull } from '../../../../api/dermatoApi';
import {
  FOTOFINDER_REPORT_KIND,
  dermatoDocumentsApi,
  type DermatoDocument,
} from '../../../../api/dermatoDocumentsApi';
import DocumentPreviewModal from '../../../../components/common/DocumentPreviewModal';
import { useAuthStore } from '../../../../store/authStore';
import { isAdminRole } from '../../../../utils/flowAccess';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';

const { Text, Paragraph } = Typography;

/** Limita de mărime pentru raportul Fotofinder (PDF). */
const MAX_PDF_BYTES = 10 * 1024 * 1024;

const formatBytes = (bytes?: number | null): string => {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Pasul 7 — „Raport Fotofinder" (§1.1.6–1.1.9).
 *
 * Operatorul Fotofinder este un **actor extern**: registrul reține indicația
 * scanării, data raportului și PDF-ul atașat — nu cine a efectuat scanarea.
 * De aceea câmpul „Operator" (și completarea lui automată) au fost eliminate.
 */
const Step3FotofinderReport = () => {
  const { form, lesions, loading, saveForm } = useDermatoWizard();
  const { user } = useAuthStore();
  /** Ștergerea documentelor e rezervată rolurilor de administrare (vezi și backendul). */
  const canDeleteDocuments = isAdminRole(user?.role?.name);
  const [indicated, setIndicated] = useState<boolean | null>(null);
  const [reportDate, setReportDate] = useState<Dayjs | null>(null);
  const [lesionId, setLesionId] = useState<string | undefined>(undefined);
  const [documents, setDocuments] = useState<DermatoDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Documentul deschis în previzualizare (null = fereastra închisă). */
  const [previewDoc, setPreviewDoc] = useState<DermatoDocument | null>(null);
  // Modificări nesalvate (ca în restul pașilor): salvarea se face doar dacă
  // utilizatorul a schimbat ceva.
  const [touched, setTouched] = useState(false);

  // Valorile inițiale vin de pe formular (deja salvate).
  useEffect(() => {
    if (!form) return;
    setIndicated(form.fotofinderIndicated ?? null);
    setReportDate(form.fotofinderReportDate ? dayjs(form.fotofinderReportDate) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.id]);

  const loadDocuments = async () => {
    if (!form?.id) return;
    try {
      setLoadingDocuments(true);
      const list = await dermatoDocumentsApi.listDocuments(form.id);
      setDocuments(list);
    } catch {
      // Interceptorul global axios afișează deja toast-ul de eroare.
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    void loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.id]);

  const persist = async (): Promise<boolean> => {
    if (!form) return true;
    if (!touched) return true;
    if (indicated === null) {
      message.error('Bifează dacă a fost efectuată scanarea Fotofinder (DA / NU).');
      return false;
    }
    if (indicated && !reportDate) {
      message.error('Data raportului este obligatorie când indicația este DA.');
      return false;
    }
    try {
      setSaving(true);
      const patch: Partial<DermatoForm> = {
        fotofinderIndicated: indicated,
        fotofinderReportDate: indicated && reportDate ? reportDate.toISOString() : null,
      };
      const saved = await saveForm(patch);
      if (!saved) return false;
      setTouched(false);
      return true;
    } catch {
      // Interceptorul global axios afișează deja toast-ul de eroare.
      return false;
    } finally {
      setSaving(false);
    }
  };

  // „Salvează și continuă" / „Salvează draft" din layout folosesc același handler.
  useRegisterStepSave(persist);

  /** Validare client-side a fișierului: MIME PDF + limită 10 MB. */
  const beforeUpload = (file: File): boolean | string => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      message.error('Doar rapoarte PDF sunt acceptate (raport Fotofinder).');
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_PDF_BYTES) {
      message.error('Fișierul depășește limita de 10 MB.');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const customRequest = async (options: {
    file: File;
    onSuccess?: (body?: unknown) => void;
    onError?: (error?: unknown) => void;
  }) => {
    const { file, onSuccess, onError } = options;
    if (!form?.id) return;
    try {
      setUploading(true);
      await dermatoDocumentsApi.uploadDocument(
        form.id,
        file,
        FOTOFINDER_REPORT_KIND,
        lesionId ?? null,
      );
      message.success('Raportul Fotofinder a fost încărcat.');
      onSuccess?.();
      await loadDocuments();
    } catch {
      message.error('Eroare la încărcarea raportului Fotofinder.');
      onError?.();
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: DermatoDocument) => {
    if (!form?.id) return;
    try {
      await dermatoDocumentsApi.downloadDocument(form.id, doc.id, doc.fileName);
    } catch {
      message.error('Eroare la descărcarea documentului.');
    }
  };

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const handleDelete = async (doc: DermatoDocument) => {
    if (!form?.id) return;
    try {
      setDeletingId(doc.id);
      await dermatoDocumentsApi.deleteDocument(form.id, doc.id);
      message.success('Documentul a fost șters.');
      await loadDocuments();
    } catch {
      message.error('Eroare la ștergerea documentului.');
    } finally {
      setDeletingId(null);
    }
  };

  const lesionOptions = useMemo(
    () =>
      lesions
        .filter((l: LesionFull) => Boolean(l.id))
        .map((l: LesionFull) => ({
          value: l.id as string,
          label: `${l.clinicalCode} — ${l.location}`,
        })),
    [lesions],
  );

  if (loading || !form) {
    return <Card loading title="Raport Fotofinder" />;
  }

  return (
    <Card
      title={
        <Space>
          <FilePdfOutlined style={{ color: '#1890ff' }} />
          <span>Raport Fotofinder</span>
        </Space>
      }
    >
      <Paragraph type="secondary">
        Persoana care a efectuat scanarea este un actor extern. Registrul reține
        <strong> indicația scanării</strong>, <strong>data raportului</strong> și
        <strong> PDF-ul atașat</strong> — nu cine a făcut scanarea.
      </Paragraph>

      <Form layout="vertical">
        <Form.Item
          label="Indicație scanare Fotofinder"
          required
          extra="Bifează DA dacă pacientul a beneficiat de scanare Fotofinder."
        >
          <Radio.Group
            value={indicated === null ? undefined : indicated ? 'DA' : 'NU'}
            onChange={(e) => {
              setIndicated(e.target.value === 'DA');
              setTouched(true);
            }}
          >
            <Radio value="DA">DA</Radio>
            <Radio value="NU">NU</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Data raportului"
          required={indicated === true}
          extra={
            indicated === true
              ? 'Data raportului este obligatorie când indicația este DA.'
              : undefined
          }
        >
          <DatePicker
            style={{ width: '100%' }}
            value={reportDate}
            onChange={(d) => {
              setReportDate(d ?? null);
              setTouched(true);
            }}
          />
        </Form.Item>

        <Divider>Documente atașate</Divider>

        {lesions.length > 0 && (
          <Form.Item label="Leziune asociată raportului" extra="Opțional — leagă PDF-ul de o leziune din formular.">
            <Select
              allowClear
              value={lesionId}
              onChange={(v) => setLesionId(v ?? undefined)}
              placeholder="Alege leziunea asociată"
              options={lesionOptions}
            />
          </Form.Item>
        )}

        <Upload
          accept="application/pdf"
          beforeUpload={beforeUpload}
          customRequest={(options) => {
            void customRequest(options as { file: File; onSuccess?: (b?: unknown) => void; onError?: (e?: unknown) => void });
          }}
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />} loading={uploading}>
            Încarcă raport Fotofinder (PDF, max. 10 MB)
          </Button>
        </Upload>

        <Form.Item style={{ marginTop: 16 }}>
          <Space>
            <Button type="primary" onClick={() => void persist()} loading={saving} disabled={!touched}>
              Salvează raport
            </Button>
            {touched && <Text type="secondary">Modificări nesalvate</Text>}
          </Space>
        </Form.Item>
      </Form>

      <Divider>Lista documentelor</Divider>

      {loadingDocuments ? (
        <Alert type="info" message="Se încarcă documentele…" />
      ) : documents.length === 0 ? (
        <Empty description="Nu există documente atașate încă." />
      ) : (
        <Table
          rowKey="id"
          size="small"
          dataSource={documents}
          pagination={false}
          columns={[
            {
              title: 'Nume fișier',
              dataIndex: 'fileName',
              // Numele lung nu se rupe în mijlocul cuvântului; valoarea completă
              // rămâne în tooltip (atributul `title`).
              ellipsis: true,
              render: (v: string) => <Text strong>{v}</Text>,
            },
            {
              title: 'Mărime',
              dataIndex: 'sizeBytes',
              width: 100,
              render: (v: number) => formatBytes(v),
            },
            {
              title: 'Data încărcare',
              dataIndex: 'uploadedAt',
              width: 160,
              render: (v?: string | null) => (v ? new Date(v).toLocaleString('ro-RO') : '—'),
            },
            {
              title: '',
              width: 280,
              render: (_: unknown, doc: DermatoDocument) => (
                <Space size={4}>
                  <Button
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => setPreviewDoc(doc)}
                    data-testid="view-document"
                  >
                    Vezi
                  </Button>
                  <Button size="small" onClick={() => void handleDownload(doc)}>
                    Descarcă
                  </Button>
                  {canDeleteDocuments && (
                    <Popconfirm
                      title="Ștergi documentul?"
                      description="Fișierul va fi eliminat din formular. Operațiunea nu poate fi anulată."
                      okText="Șterge"
                      okButtonProps={{ danger: true }}
                      cancelText="Renunță"
                      onConfirm={() => void handleDelete(doc)}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        loading={deletingId === doc.id}
                        data-testid="delete-document"
                      >
                        Șterge
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
        />
      )}

      <DocumentPreviewModal
        open={previewDoc !== null}
        formId={form.id}
        documentId={previewDoc?.id ?? null}
        fileName={previewDoc?.fileName ?? null}
        onClose={() => setPreviewDoc(null)}
      />
    </Card>
  );
};

export default Step3FotofinderReport;
